/**
 * Persistence for the user's keymap overrides — the half of the command system
 * that makes "commands separate from bindings" worth the separation.
 *
 * Only overrides are stored, never the resolved keymap. Storing the resolved
 * map would freeze every default at the moment the user first touched any
 * shortcut: change a default in `DEFAULT_KEYMAP` later and nobody who had ever
 * opened the shortcuts panel would receive it. A sparse override map means a
 * user who rebound one command still tracks the defaults for the other
 * fourteen.
 *
 * Reads are validated rather than trusted. This blob is user-writable (it is
 * localStorage) and survives across app versions, so it can name a command
 * that no longer exists or hold a chord this app refuses to register. Anything
 * that fails validation is dropped, quietly and per-entry — a single bad row
 * must not cost the user the rest of their customizations.
 */
import { safeParseJSON, STORAGE_KEYS } from '@/utils/sessionStorage';
import { isCommandId, isRegistrableBinding } from './keymap';
import type { Binding, CommandId, KeymapOverrides } from './types';

/** Shape actually written to disk, so the schema can be versioned. */
interface StoredKeymap {
  readonly version: 1;
  readonly overrides: Record<string, unknown>;
}

const VERSION = 1;

function readRaw(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(STORAGE_KEYS.KEYBOARD_KEYMAP);
  } catch {
    // Private-mode / blocked storage. Defaults still work.
    return null;
  }
}

/**
 * Validate one stored value into a binding list.
 *
 * Returns null (drop the entry) rather than a partial list when the value is
 * not an array at all; within an array, individual bad bindings are skipped.
 * An empty result is meaningful and preserved: it is how a user records
 * "unbound", and collapsing it to null would resurrect the default.
 */
function parseBindings(value: unknown): readonly Binding[] | null {
  if (!Array.isArray(value)) return null;
  const bindings: Binding[] = [];
  for (const entry of value) {
    // Decide the SHAPE first, then validate once. Writing the
    // `isRegistrableBinding` push twice — once per shape — is two places for
    // the policy to drift.
    const shaped =
      typeof entry === 'string' ||
      (Array.isArray(entry) &&
        entry.length > 0 &&
        entry.every((step) => typeof step === 'string'));
    if (shaped && isRegistrableBinding(entry as Binding)) {
      bindings.push(entry as Binding);
    }
  }
  return bindings;
}

/**
 * The user's stored overrides, or `{}`.
 *
 * Safe to call before React mounts and in node-env tests — every storage touch
 * is guarded and wrapped.
 */
export function loadKeymapOverrides(): KeymapOverrides {
  const raw = readRaw();
  if (!raw) return {};

  // `safeParseJSON` is the repo's guarded parse (`@/utils/sessionStorage`);
  // `authoringDraft.ts` already pairs it with `STORAGE_KEYS` the same way.
  const parsed = safeParseJSON<unknown>(raw, null);
  if (typeof parsed !== 'object' || parsed === null) return {};
  const stored = parsed as Partial<StoredKeymap>;
  // An unknown version is a future schema this build cannot read. Ignoring it
  // is better than guessing: the user keeps the defaults, and the newer build
  // they came from still reads its own blob.
  if (stored.version !== VERSION) return {};
  if (typeof stored.overrides !== 'object' || stored.overrides === null) {
    return {};
  }

  const overrides: Partial<Record<CommandId, readonly Binding[]>> = {};
  for (const [id, value] of Object.entries(stored.overrides)) {
    if (!isCommandId(id)) continue;
    const bindings = parseBindings(value);
    if (bindings) overrides[id] = bindings;
  }
  return overrides;
}

/** Write the overrides. Best-effort: a quota or privacy error is swallowed. */
export function saveKeymapOverrides(overrides: KeymapOverrides): void {
  if (typeof window === 'undefined') return;
  const payload: StoredKeymap = {
    version: VERSION,
    overrides: overrides as Record<string, unknown>,
  };
  try {
    window.localStorage.setItem(
      STORAGE_KEYS.KEYBOARD_KEYMAP,
      JSON.stringify(payload),
    );
  } catch {
    // Best-effort only — the in-memory keymap is still correct for this
    // session, so a failed write costs persistence, not function.
  }
}
